import { computed, type ComputedRef } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { storeToRefs } from 'pinia';
import { getMetadataAutomations } from '@/common/api/api.pleased';
import { useBotUser } from '@/stores/useBotUser';
import type { MetadataRule } from '@/common/api/models.pleased';
import { useMockedFlowStore } from '@/library/cf-v1/mocked-flow';

/**
 * TanStack Query hook to fetch metadata automation rules for an article
 * Only fetches when user is not a guest (logged in)
 *
 * @param articleId - Article ID to fetch metadata rules for
 * @returns TanStack Query result with metadata rules
 *
 * @example
 * ```ts
 * const articleId = computed(() => Number(route.params.id));
 * const { data: metadataRules } = useMetadataAutomations(articleId);
 * ```
 */
export const useMetadataAutomations = (articleId: ComputedRef<string | number | undefined>) => {
  const { user } = storeToRefs(useBotUser());
  const isLoggedIn = computed(() => !user.value.isGuest);

  const mockedFlowStore = useMockedFlowStore();
  const { isActive: isMockedFlowActive } = storeToRefs(mockedFlowStore);

  return useQuery({
    queryKey: ['metadataAutomations', articleId],
    queryFn: async (): Promise<MetadataRule[]> => {
      if (isMockedFlowActive.value) return [];

      if (!articleId.value) {
        throw new Error('Article ID is required');
      }

      const response = await getMetadataAutomations.cached({
        articleId: articleId.value,
      });
      return response.data;
    },
    enabled: computed(() => !!articleId.value && isLoggedIn.value),
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    retry: false, // Don't retry if metadata automations fail
  });
};
